import Cross from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import Tick from '@mui/icons-material/Done';
import Email from '@mui/icons-material/Email';
import InfoIcon from '@mui/icons-material/Info';
import Phone from '@mui/icons-material/Phone';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Tooltip,
  Typography,
} from '@mui/material';
import CircularProgress from '@mui/material/CircularProgress';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Unstable_Grid2';
import { setError, setOk } from 'client/modules/App/AppActions';
import { getProfile } from 'client/modules/Auth/AuthReducer';
import SecondaryNavTabs from 'client/modules/Surveys/pages/DashboardPage/SecondaryNavTabs';
import { getSurveys } from 'client/modules/Surveys/SurveyReducer';
import Layout from 'components/Layout/LayoutContainer';
import NotFound from 'components/NotFound/NotFound';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useElementSize } from 'use-element-size';
import {
  useDeletePersonMutation,
  useGetActivitiesQuery,
  useGetPersonQuery,
} from '../../peopleApiSlice';
import messages from './messages';
import styles from './styles';

function PersonView() {
  const dispatch = useDispatch();
  const [outerHeight, setOuterHeight] = useState(0);
  const rowHeight = 48;
  const outerRef = useElementSize(size => {
    setOuterHeight(size.height - 20);
  });
  const _surveys = useSelector(getSurveys);
  const profile = useSelector(getProfile);
  const { id: survey, personId } = useParams();
  const { formatMessage, locale } = useIntl();
  const navigate = useNavigate();
  const { returnLinkTo, returnLinkText } = useLocation().state || {
    returnLinkTo: `/surveys/${survey}/people/?status=active`,
    returnLinkText: formatMessage(messages.back),
  };
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const [deletePerson] = useDeletePersonMutation();
  const [deleteDialog, setDeleteDialog] = useState({ open: false });
  useEffect(() => {
    if (rowHeight && outerHeight) {
      setLimit(Math.floor(outerHeight / rowHeight));
    }
  }, [rowHeight, outerHeight]);
  const { data: person, isLoading: isLoadingPerson } = useGetPersonQuery({ survey, personId });
  const { data: activities, isLoading: isLoadingActivities } = useGetActivitiesQuery({
    survey,
    personId,
    limit,
    skip: (page - 1) * limit,
  });
  const pages = Math.ceil(activities?.totalDocs / limit) || 1;
  useEffect(() => {
    if (page > pages) {
      setPage(pages);
    }
  }, [limit, page, pages]);
  const handlePageChange = useCallback((event, newPage) => setPage(newPage), [setPage]);

  const handleDeletePerson = useCallback(() => {
    setDeleteDialog({ open: true });
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    try {
      await deletePerson({ survey, personId }).unwrap();
      dispatch(setOk(formatMessage(messages.deletePersonSuccess), 3000));
      // Close the dialog
      setDeleteDialog({ open: false });
      // Redirect to people list after a short delay to show success message
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error) {
      console.error('Failed to delete person:', error);
      dispatch(setError(formatMessage(messages.deletePersonError), 5000));
    }
  }, [deletePerson, survey, personId, dispatch, formatMessage, navigate]);

  const handleCancelDelete = useCallback(() => {
    setDeleteDialog({ open: false });
  }, []);

  const isLoading = isLoadingPerson || isLoadingActivities;
  if (!isLoadingPerson && !person) {
    return <NotFound />;
  }
  let status;
  if (person?.unsubscribed_at) {
    status = formatMessage(messages.personStatusUnsubscribed);
  } else if (person?.bounced_at) {
    status = formatMessage(messages.personStatusBounced);
  } else if (person?.blocked_at) {
    status = formatMessage(messages.personStatusBlocked);
  }

  return (
    <Layout
      returnLinkTo={returnLinkTo}
      returnLinkText={returnLinkText}
      secondaryNav={<SecondaryNavTabs survey={survey} />}
    >
      <Box sx={styles.root}>
        <Typography variant="h1">{person?.name || person?.email}</Typography>
        <Grid container spacing={2} sx={styles.container}>
          <Grid xs={4}>
            {person && (
              <Box sx={styles.personInfo}>
                {!!person.email && (
                  <Box component="a" href={`mailto:${person.email}`}>
                    <Email />
                    <Typography variant="body1">{person.email}</Typography>
                  </Box>
                )}
                {!!person.phone && (
                  <Box>
                    <Phone />
                    <Typography variant="body1">{person.phone}</Typography>
                  </Box>
                )}
                <Box component="dl" sx={styles.defs}>
                  <Box component="dt" sx={styles.status(status)}>
                    {formatMessage(messages.status)}
                    <Tooltip
                      title={formatMessage(messages.statusHint)}
                      arrow
                      placement="right-start"
                    >
                      <InfoIcon />
                    </Tooltip>
                  </Box>
                  <Box component="dd">
                    {status || formatMessage(messages.personStatusActive)}
                    {status ? <Cross /> : <Tick />}
                  </Box>
                  <dt>{formatMessage(messages.timesSurveyed)}</dt>
                  <dd>{+person.totalSendCount}</dd>
                  <dt>{formatMessage(messages.lastSurveyed)}</dt>
                  <dd>
                    {person.lastSurveyed
                      ? `${moment(person.lastSurveyed).locale(locale).format('LL')} - ${moment(
                          person.lastSurveyed,
                        )
                          .locale(locale)
                          .format('LT')}`
                      : '-'}
                  </dd>
                </Box>
                {profile?.admin && (
                  <Box sx={{ mt: 2 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={handleDeletePerson}
                      sx={{ width: '100%' }}
                    >
                      {formatMessage(messages.deletePerson)}
                    </Button>
                  </Box>
                )}
              </Box>
            )}
          </Grid>
          <Grid xs={8} sx={styles.outerGrid}>
            {isLoading && (
              <Box sx={styles.circularBackdrop}>
                <CircularProgress />
              </Box>
            )}
            {!isLoading && !activities?.totalDocs && (
              <Typography variant="body1">{formatMessage(messages.noActivities)}</Typography>
            )}
            {!isLoading && activities?.totalDocs > 0 && (
              <>
                <Box component="ul" sx={{ ...styles.listTimeline, ...styles.outer }} ref={outerRef}>
                  {activities.docs.map(activity => {
                    const timestamp = moment(activity.timestamp).locale(locale);
                    return (
                      <Box
                        component="li"
                        sx={{ ...styles.listTimelineItem, ...styles.item }}
                        key={activity._id}
                      >
                        <Typography>
                          {formatMessage(messages[`status_${activity.action}`])}
                          <Box sx={styles.badge}>{activity.metadata.channel}</Box>
                        </Typography>
                        <Typography color="muted.main">{`${timestamp.format('LL')} - ${timestamp.format('LT')}`}</Typography>
                      </Box>
                    );
                  })}
                </Box>
                <Box sx={{ display: 'flex', gap: 4, alignItems: 'baseline' }}>
                  <Pagination count={pages} page={page} onChange={handlePageChange} />
                  {formatMessage(messages.disclaimer)}
                </Box>
              </>
            )}
          </Grid>
        </Grid>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={handleCancelDelete}>
        <DialogTitle>{formatMessage(messages.deletePersonTitle)}</DialogTitle>
        <DialogContent>
          <DialogContentText>{formatMessage(messages.deletePersonConfirm)}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>{formatMessage(messages.cancel)}</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            {formatMessage(messages.deletePerson)}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default PersonView;
